import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CommentItem, Comment } from "./CommentItem";

interface CommentsListProps {
  comments: Comment[];
  loading: boolean;
  pageSize?: number;
  onReply?: (commentId: string, username: string) => void;
}

export function CommentsList({ comments, loading, pageSize = 8, onReply }: CommentsListProps) {
  const [visibleCount, setVisibleCount] = useState(pageSize);

  const visibleComments = comments.slice(0, visibleCount);
  const hasMore = visibleCount < comments.length;

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + pageSize);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex items-center gap-2 text-white/50">
          <div className="w-4 h-4 border-2 border-brand-cyan/30 border-t-brand-cyan rounded-full animate-spin" />
          <span className="text-sm">Loading comments...</span>
        </div>
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-12 h-12 rounded-full bg-brand-cyan/10 flex items-center justify-center mb-3">
          <MessageSquare className="h-5 w-5 text-brand-cyan/50" />
        </div>
        <p className="text-white/50 text-sm mb-1">No comments yet</p>
        <p className="text-white/30 text-xs">Be the first to start the conversation!</p>
      </div>
    );
  }

  return (
    <div className="px-4 pb-4">
      <div className="divide-y divide-brand-cyan/10">
        {visibleComments.map((comment, index) => (
          <CommentItem 
            key={comment.id} 
            comment={comment} 
            index={index}
            onReply={onReply}
          />
        ))}
      </div>

      {hasMore && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-4 text-center"
        >
          <Button
            variant="ghost"
            onClick={handleLoadMore}
            className="text-brand-cyan/70 hover:text-brand-cyan hover:bg-brand-cyan/10 transition-colors"
          >
            <ChevronDown className="h-4 w-4 mr-2" />
            View more comments ({comments.length - visibleCount} remaining)
          </Button>
        </motion.div>
      )}
    </div>
  );
}
